import React, { useState, useEffect } from 'react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { createCompany, updateCompany } from '../../../services/companyService';
import { fetchAllCurrencies, formatCurrencyOptions, detectUserCurrency } from '../../../services/currencyService';

const CompanyModal = ({ isOpen, onClose, onSubmit, company }) => {
  const [formData, setFormData] = useState({
    businessName: '',
    legalName: '',
    taxId: '',
    contactPerson: '',
    contactEmail: '',
    contactPhone: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    country: 'United States',
    currency: 'USD',
    subscriptionTier: 'Starter',
    dataIsolation: true,
    activeUsers: 1,
    // Administrator fields
    adminFullName: '',
    adminEmail: '',
    adminPassword: ''
  });
  const [loading, setLoading] = useState(false);
  const [currencies, setCurrencies] = useState([]);
  const [loadingCurrencies, setLoadingCurrencies] = useState(true);

  // Load currencies on mount
  useEffect(() => {
    const loadCurrencies = async () => {
      try {
        setLoadingCurrencies(true);
        const currencyList = await fetchAllCurrencies();
        setCurrencies(currencyList);
        
        // Auto-detect user's currency if creating new company
        if (!company && formData?.currency === 'USD') {
          const detectedCurrency = await detectUserCurrency();
          if (detectedCurrency && detectedCurrency !== 'USD') {
            setFormData(prev => ({ ...prev, currency: detectedCurrency }));
          }
        }
      } catch (error) {
        console.error('Error loading currencies:', error);
      } finally {
        setLoadingCurrencies(false);
      }
    };

    if (isOpen) {
      loadCurrencies();
    }
  }, [isOpen, company]);

  useEffect(() => {
    if (company) {
      setFormData({
        businessName: company?.businessName || '',
        legalName: company?.legalName || '',
        taxId: company?.taxId || '',
        contactPerson: company?.contactPerson || '',
        contactEmail: company?.contactEmail || '',
        contactPhone: company?.contactPhone || '',
        address: company?.address || '',
        city: company?.city || '',
        state: company?.state || '',
        zipCode: company?.zipCode || '',
        country: company?.country || 'United States',
        currency: company?.currency || 'USD',
        subscriptionTier: company?.subscriptionTier || 'Starter',
        dataIsolation: company?.dataIsolation ?? true,
        activeUsers: company?.activeUsers || 1,
        adminFullName: '',
        adminEmail: '',
        adminPassword: ''
      });
    } else {
      setFormData({
        businessName: '',
        legalName: '',
        taxId: '',
        contactPerson: '',
        contactEmail: '',
        contactPhone: '',
        address: '',
        city: '',
        state: '',
        zipCode: '',
        country: 'United States',
        currency: 'USD',
        subscriptionTier: 'Starter',
        dataIsolation: true,
        activeUsers: 1,
        adminFullName: '',
        adminEmail: '',
        adminPassword: ''
      });
    }
  }, [company, isOpen]);

  const subscriptionOptions = [
    { value: 'Starter', label: 'Starter - $29/month' },
    { value: 'Professional', label: 'Professional - $99/month' },
    { value: 'Enterprise', label: 'Enterprise - Custom pricing' }
  ];

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setLoading(true);
    try {
      if (company) {
        await updateCompany(company?.id, formData);
      } else {
        await createCompany(formData);
      }
      onSubmit();
      onClose();
    } catch (error) {
      console.error('Error saving company:', error);
      alert('Failed to save company. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const currencyOptions = formatCurrencyOptions(currencies);

  return (
    <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-1030 flex items-center justify-center p-4">
      <div className="bg-card rounded-lg shadow-2xl border border-border w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between p-4 md:p-6 border-b border-border">
          <h2 className="font-heading font-semibold text-xl md:text-2xl text-foreground">
            {company ? 'Edit Company' : 'Add New Company'}
          </h2>
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            iconName="X"
            aria-label="Close modal"
          />
        </div>

        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-4 md:p-6">
          <div className="space-y-6">
            <div>
              <h3 className="font-heading font-semibold text-base text-foreground mb-4">Business Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  label="Business Name"
                  value={formData?.businessName}
                  onChange={(e) => setFormData({ ...formData, businessName: e?.target?.value })}
                  required
                  placeholder="Enter business name"
                />
                <Input
                  label="Legal Name"
                  value={formData?.legalName}
                  onChange={(e) => setFormData({ ...formData, legalName: e?.target?.value })}
                  required
                  placeholder="Enter legal name"
                />
                <Input
                  label="Tax ID / EIN"
                  value={formData?.taxId}
                  onChange={(e) => setFormData({ ...formData, taxId: e?.target?.value })}
                  placeholder="XX-XXXXXXX"
                />
                <Select
                  label="Base Currency"
                  options={currencyOptions}
                  value={formData?.currency}
                  onChange={(value) => setFormData({ ...formData, currency: value })}
                  required
                  disabled={loadingCurrencies}
                  placeholder={loadingCurrencies ? 'Loading currencies...' : 'Select currency'}
                />
              </div>
            </div>

            <div>
              <h3 className="font-heading font-semibold text-base text-foreground mb-4">Contact Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  label="Contact Person"
                  value={formData?.contactPerson}
                  onChange={(e) => setFormData({ ...formData, contactPerson: e?.target?.value })}
                  required
                  placeholder="Enter contact name"
                />
                <Input
                  label="Contact Email"
                  type="email"
                  value={formData?.contactEmail}
                  onChange={(e) => setFormData({ ...formData, contactEmail: e?.target?.value })}
                  required
                  placeholder="contact@company.com"
                />
                <Input
                  label="Contact Phone"
                  value={formData?.contactPhone}
                  onChange={(e) => setFormData({ ...formData, contactPhone: e?.target?.value })}
                  placeholder="(555) 123-4567"
                />
              </div>
            </div>

            <div>
              <h3 className="font-heading font-semibold text-base text-foreground mb-4">Address</h3>
              <div className="grid grid-cols-1 gap-4">
                <Input
                  label="Street Address"
                  value={formData?.address}
                  onChange={(e) => setFormData({ ...formData, address: e?.target?.value })}
                  placeholder="Enter street address"
                />
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <Input
                    label="City"
                    value={formData?.city}
                    onChange={(e) => setFormData({ ...formData, city: e?.target?.value })}
                    placeholder="City"
                  />
                  <Input
                    label="State"
                    value={formData?.state}
                    onChange={(e) => setFormData({ ...formData, state: e?.target?.value })}
                    placeholder="State"
                  />
                  <Input
                    label="ZIP Code"
                    value={formData?.zipCode}
                    onChange={(e) => setFormData({ ...formData, zipCode: e?.target?.value })}
                    placeholder="12345"
                  />
                </div>
                <Input
                  label="Country"
                  value={formData?.country}
                  onChange={(e) => setFormData({ ...formData, country: e?.target?.value })}
                  placeholder="Country"
                />
              </div>
            </div>

            <div>
              <h3 className="font-heading font-semibold text-base text-foreground mb-4">Subscription & Settings</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Select
                  label="Subscription Tier"
                  value={formData?.subscriptionTier}
                  onChange={(value) => setFormData({ ...formData, subscriptionTier: value })}
                  options={subscriptionOptions}
                />
                <div className="flex items-center gap-2 pt-6">
                  <input
                    type="checkbox"
                    id="dataIsolation"
                    checked={formData?.dataIsolation}
                    onChange={(e) => setFormData({ ...formData, dataIsolation: e?.target?.checked })}
                    className="w-4 h-4 text-primary border-border rounded focus:ring-primary"
                  />
                  <label htmlFor="dataIsolation" className="text-sm text-foreground">
                    Enable Data Isolation
                  </label>
                </div>
              </div>
            </div>

            {!company && (
              <div>
                <h3 className="font-heading font-semibold text-base text-foreground mb-4">Default Administrator</h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Create a default administrator account for this company. This user will have full access to manage the company.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Input
                    label="Administrator Full Name"
                    value={formData?.adminFullName}
                    onChange={(e) => setFormData({ ...formData, adminFullName: e?.target?.value })}
                    required={!company}
                    placeholder="Enter administrator name"
                  />
                  <Input
                    label="Administrator Email"
                    type="email"
                    value={formData?.adminEmail}
                    onChange={(e) => setFormData({ ...formData, adminEmail: e?.target?.value })}
                    required={!company}
                    placeholder="admin@company.com"
                  />
                  <Input
                    label="Administrator Password"
                    type="password"
                    value={formData?.adminPassword}
                    onChange={(e) => setFormData({ ...formData, adminPassword: e?.target?.value })}
                    required={!company}
                    placeholder="Enter secure password"
                  />
                </div>
              </div>
            )}
          </div>
        </form>

        <div className="flex items-center justify-end gap-3 p-4 md:p-6 border-t border-border">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            variant="default"
            onClick={handleSubmit}
            disabled={loading}
            iconName={loading ? 'Loader2' : 'Save'}
          >
            {loading ? 'Saving...' : company ? 'Update Company' : 'Create Company'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CompanyModal;
